import { clone, isFunction } from 'lodash-es'
import type { IField } from '../type'
import { useAdminStore } from '@/store/adminData'
import { useUserStore } from '@/store/userData'
import { getArticleStates, getAssignedCatalogModels } from '@/api/t1/article'
import { getCatalogColors, getCatalogShipmentWindows } from '@/api/t1/catalog'
import { getCatalogSizeScales, getMasterSizeScales } from '@/api/t1/size'
import type Article from '@/models/article'
import type CatalogDetails from '@/models/catalogDetails'
import appConfig from '@/services/appConfig'
import utils from '@/services/utils'
import { AttributeType } from '@/models/catalogAttribute'
import type { ITxDataTableColumn } from '@/shared/components/txDataTable/TxDataTable.types'
import type { SellerColorModel } from '@/api/t1/model/catalog'
import { getDatasetValues } from '@/api/t1/datasets'

export default function useMasterDataLookup() {
  const adminStore = useAdminStore()
  const userStore = useUserStore()

  // TODO: Need to merge both the functions loadLookupForTable and loadLookup as both are doing same
  async function loadLookupForTable(fields: ITxDataTableColumn[], activeCatalog: CatalogDetails, reload = false) {
    const dataMap = {}
    const lookupSourceMap = {}
    const lookupFields: ITxDataTableColumn[] = []
    fields.forEach((field) => {
      if ((field.type === AttributeType.Lookup
        || field.type === AttributeType.LookupMany
        || field.type === AttributeType.ModelNumber
        || field.type === AttributeType.ColorPalette
      )
      && (utils.isDefined(field.lookupSource) && isFunction(field.lookupSource))) {
        lookupFields.push(field)
        if (lookupSourceMap[field.lookupSource.toString()]) {
          lookupSourceMap[field.lookupSource.toString()].fields.push(field.property)
        }
        else {
          lookupSourceMap[field.lookupSource.toString()] = { fields: [field.property], action: field.lookupSource }
        }
      }
    })

    const lookupPromises = Object.keys(lookupSourceMap).map(lookupSourceString =>
      lookupSourceMap[lookupSourceString].action(activeCatalog, reload).then((data) => {
        lookupSourceMap[lookupSourceString].fields.forEach((field) => {
          dataMap[field] = data
        })
      }),
    )
    // promises to resolve
    // check with saad when loading what needed to be done in UI
    // check the performance when testing if any lag we can show a loading
    return await Promise.all(lookupPromises).then(() => {
      console.log('All lookup data loaded:')
      return dataMap
    }).catch((error) => {
      console.error('Error loading lookup data:', error)
      return dataMap
    })
  }
  async function loadLookup(fields: IField[], activeCatalog: CatalogDetails, reload = false) {
    const dataMap = {}
    const lookupSourceMap = new Map<Function, { fields: string[], datasetIds: Set<number | undefined>, action: Function }>()
    const lookupFields: IField[] = []
    fields.forEach((field) => {
      if ((field.AttributeType === AttributeType.Lookup
        || field.AttributeType === AttributeType.LookupMany
        || field.AttributeType === AttributeType.ModelNumber
        || field.AttributeType === AttributeType.ColorPalette
        || field.AttributeType === AttributeType.Dataset
      )
      && (utils.isDefined(field.lookupSource) && isFunction(field.lookupSource))) {
        lookupFields.push(field)
        const datasetId = field.AttributeType === AttributeType.Dataset ? field.DatasetId : undefined

        if (lookupSourceMap.has(field.lookupSource)) {
          const entry = lookupSourceMap.get(field.lookupSource)!
          entry.fields.push(field.SystemName)
          if (datasetId) { entry.datasetIds.add(datasetId) }
        }
        else {
          lookupSourceMap.set(field.lookupSource, { fields: [field.SystemName], datasetIds: new Set([datasetId]), action: field.lookupSource })
        }
      }
    })

    const lookupPromises: Promise<void>[] = []
    lookupSourceMap.forEach(({ fields, datasetIds, action }) => {
      datasetIds.forEach((datasetId) => {
        lookupPromises.push(
          action(activeCatalog, datasetId, reload).then((data: any) => {
            fields.forEach((field) => {
              dataMap[field] = data
            })
          }),
        )
      })
    })
    // promises to resolve
    // check with saad when loading what needed to be done in UI
    // check the performance when testing if any lag we can show a loading
    return await Promise.all(lookupPromises).then(() => {
      console.log('All lookup data loaded:')
      return dataMap
    }).catch((error) => {
      console.error('Error loading lookup data:', error)
      return dataMap
    })
  }

  async function getModelsLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.catalogModels) || reload) {
      const response = await getAssignedCatalogModels(catalogDetails.CatalogCode)
      adminStore.lookupData.catalogModels = utils.arrayToNumberDictionary(response.data, 'ModelId')
    }

    return adminStore.lookupData.catalogModels
  }

  async function getSizeScalesLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.sizeScales) || reload) {
      const response = await getCatalogSizeScales(catalogDetails.CatalogCode)
      adminStore.lookupData.sizeScales = utils.arrayToNumberDictionary(response.data, 'Id')
    }
    return adminStore.lookupData.sizeScales
  }

  async function getMasterSizeScalesLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.masterSizeScales) || reload) {
      const data = await getMasterSizeScales(catalogDetails.AccountId)
      adminStore.lookupData.masterSizeScales = utils.arrayToNumberDictionary(data, 'Id')
    }
    return adminStore.lookupData.masterSizeScales
  }

  async function getRetailWindowsLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    // TODO implement parent article filter for child catalog when implementing Edit article action
    // for import its not needed.
    if (!utils.isDefined(adminStore.lookupData.retailWindows) || reload) {
      const response = await getCatalogShipmentWindows(catalogDetails.CatalogCode)
      adminStore.lookupData.retailWindows = utils.arrayToNumberDictionary(response.data, 'Id')
    }
    return adminStore.lookupData.retailWindows
  }

  async function getArticleStatesLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.articleStates) || reload) {
      const data = await getArticleStates(catalogDetails.AccountId, catalogDetails.AttributeGroupId)
      adminStore.lookupData.articleStates = utils.arrayToNumberDictionary(data, 'StateId')
    }
    return adminStore.lookupData.articleStates
  }

  async function getColorsLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.colors) || reload) {
      const response = await getCatalogColors(catalogDetails.CatalogCode)
      const uniqueColors: SellerColorModel[] = []
      response.data.forEach((sellerColor) => {
        if (sellerColor.Status) { // consider active palette only
          const colorCriteria = sellerColor.ColorCriteria

          for (let j = 0; j < sellerColor.Colors.length; j++) {
            const color = clone(sellerColor.Colors[j])
            color.ColorCriteria = colorCriteria

            if (!uniqueColors.some((existingColor) => { return existingColor.ColorId === color.ColorId })) {
              uniqueColors.push(color)
            }
          }
        }
      })
      adminStore.lookupData.colors = utils.arrayToNumberDictionary(uniqueColors, 'ColorName')
    }
    return adminStore.lookupData.colors
  }

  async function getArticlesLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.articles) || reload) {
      const data = await appConfig.DB!.getArticlesByCatalogCodeStatus(catalogDetails, true)
      adminStore.lookupData.articles = utils.arrayToNumberDictionary(data, 'Id')
    }
    return adminStore.lookupData.articles
  }

  async function getLinkedCatalogArticleNumberAttributesLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.linkedCatalogArticles) || reload) {
      if (utils.isDefined(catalogDetails.LinkedCatalogList) && catalogDetails.LinkedCatalogList.length) {
        const articlePromises: Promise<Article[]>[] = []
        catalogDetails.LinkedCatalogList.forEach((catalog) => {
          const linkedCatalogDetails = userStore.linkedCatalogDetails[catalog.CatalogCode] || userStore.otherLinkedCatalogDetails[catalog.CatalogCode]
          if (linkedCatalogDetails) {
            articlePromises.push(appConfig.DB!.getArticlesByCatalogCodeStatus(linkedCatalogDetails, true))
          }
        })
        const linkedCatalogArticlesArray = await Promise.all(articlePromises)
        adminStore.lookupData.linkedCatalogArticles = utils.arrayToNumberDictionary(linkedCatalogArticlesArray.flat(), 'Id')
      }
      else {
        adminStore.lookupData.linkedCatalogArticles = {}
      }
    }
    return adminStore.lookupData.linkedCatalogArticles
  }

  async function getSellerModelsLookup(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.sellerModels) || reload) {
      const data = await appConfig.DB!.getArticlesByCatalogCodeStatus(catalogDetails, true, true)
      const uniqueModelNumbers = new Set<string>()
      const result: Article[] = []
      data.forEach((article) => {
        if (!uniqueModelNumbers.has(article.ModelNumber)) {
          result.push(article)
          uniqueModelNumbers.add(article.ModelNumber)
        }
      })
      adminStore.lookupData.sellerModels = utils.arrayToNumberDictionary(result, 'ModelId')
    }
    return adminStore.lookupData.sellerModels
  }

  async function getDropReasons(catalogDetails: CatalogDetails, _datasetId?: number, reload = false) {
    if (!utils.isDefined(adminStore.lookupData.dropReasons) || reload) {
      const data = await appConfig.DB!.getArticleDropReasons(catalogDetails.AccountId, 1)
      adminStore.lookupData.dropReasons = utils.arrayToNumberDictionary(data, 'Id')
    }
    return adminStore.lookupData.dropReasons
  }

  function setLookupDataFromArticles(articles: Article[]) {
    adminStore.lookupData.articles = {}
    adminStore.lookupData.sellerModels = {}

    const uniqueModelNumbers = new Set<string>()
    articles.forEach((article) => {
      if (!uniqueModelNumbers.has(article.ModelNumber)) {
        adminStore.lookupData.sellerModels![article.ModelId] = article
      }
      adminStore.lookupData.articles![article.Id] = article
    })
  }

  async function getSellerDatasetValuesLookup(catalogDetails: CatalogDetails, datasetId?: number, reload = false) {
    if (!datasetId) { return {} }

    if (!utils.isDefined(adminStore.lookupData.datasetValues)) {
      adminStore.lookupData.datasetValues = {}
    }

    if (!utils.isDefined(adminStore.lookupData.datasetValues[datasetId]) || reload) {
      const data = await getDatasetValues(catalogDetails.AccountId, datasetId)
      adminStore.lookupData.datasetValues[datasetId] = utils.arrayToNumberDictionary(data as any, 'DisplayValue')
    }

    return adminStore.lookupData.datasetValues[datasetId]
  }

  return {
    loadLookup,
    loadLookupForTable,
    setLookupDataFromArticles,
    getModelsLookup,
    getSizeScalesLookup,
    getMasterSizeScalesLookup,
    getRetailWindowsLookup,
    getArticleStatesLookup,
    getColorsLookup,
    getArticlesLookup,
    getLinkedCatalogArticleNumberAttributesLookup,
    getSellerModelsLookup,
    getDropReasons,
    getSellerDatasetValuesLookup,
  }
}
